export interface AddressSuggestion {
  placeId: string;
  primaryText: string;
  secondaryText: string;
  fullText: string;
}

export interface ResolvedAddress {
  line1: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  formattedAddress: string;
}

interface GoogleAutocompletePrediction {
  placeId?: string;
  text?: {
    text?: string;
  };
  structuredFormat?: {
    mainText?: {
      text?: string;
    };
    secondaryText?: {
      text?: string;
    };
  };
}

interface GoogleAutocompleteResponse {
  suggestions?: Array<{
    placePrediction?: GoogleAutocompletePrediction;
  }>;
}

interface GoogleAddressComponent {
  longText?: string;
  shortText?: string;
  types?: string[];
}

interface GooglePlaceDetailsResponse {
  formattedAddress?: string;
  addressComponents?: GoogleAddressComponent[];
}

const PLACES_BASE_URL = "https://places.googleapis.com/v1";

const extractApiErrorMessage = async (response: Response): Promise<string> => {
  try {
    const payload = (await response.json()) as {
      error?: {
        message?: string;
        status?: string;
      };
    };
    const message = payload.error?.message || payload.error?.status;
    return message ? `${response.status} ${message}` : `${response.status}`;
  } catch {
    return `${response.status}`;
  }
};

const getComponentText = (
  components: GoogleAddressComponent[],
  type: string,
): string => {
  const found = components.find((component) => component.types?.includes(type));
  return (found?.longText || "").trim();
};

const parseAddress = (payload: GooglePlaceDetailsResponse): ResolvedAddress => {
  const components = Array.isArray(payload.addressComponents)
    ? payload.addressComponents
    : [];

  const streetNumber = getComponentText(components, "street_number");
  const route = getComponentText(components, "route");
  const fallbackLine1 =
    (payload.formattedAddress || "")
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean)[0] || "";

  const line1 =
    [streetNumber, route].filter(Boolean).join(" ") || fallbackLine1;
  const city =
    getComponentText(components, "locality") ||
    getComponentText(components, "postal_town") ||
    getComponentText(components, "sublocality");
  const state = getComponentText(components, "administrative_area_level_1");
  const postalCode = getComponentText(components, "postal_code");
  const country = getComponentText(components, "country");

  return {
    line1,
    city,
    state,
    postalCode,
    country,
    formattedAddress: payload.formattedAddress || "",
  };
};

export const fetchAddressSuggestions = async (
  input: string,
  apiKey: string,
  options?: {
    regionCodes?: string[];
    languageCode?: string;
  },
): Promise<AddressSuggestion[]> => {
  const trimmedInput = input.trim();
  if (!trimmedInput) {
    return [];
  }

  const response = await fetch(`${PLACES_BASE_URL}/places:autocomplete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": apiKey,
      "X-Goog-FieldMask":
        "suggestions.placePrediction.placeId,suggestions.placePrediction.text.text,suggestions.placePrediction.structuredFormat.mainText.text,suggestions.placePrediction.structuredFormat.secondaryText.text",
    },
    body: JSON.stringify({
      input: trimmedInput,
      languageCode: options?.languageCode || "en",
      includedRegionCodes: options?.regionCodes,
    }),
  });

  if (!response.ok) {
    const details = await extractApiErrorMessage(response);
    throw new Error(`Address autocomplete failed: ${details}`);
  }

  const payload = (await response.json()) as GoogleAutocompleteResponse;
  const suggestions = Array.isArray(payload.suggestions)
    ? payload.suggestions
    : [];

  return suggestions
    .map((entry) => {
      const prediction = entry.placePrediction;
      const placeId = prediction?.placeId || "";
      const fullText = prediction?.text?.text || "";
      const primaryText =
        prediction?.structuredFormat?.mainText?.text || fullText;
      const secondaryText =
        prediction?.structuredFormat?.secondaryText?.text || "";
      if (!placeId || !fullText) {
        return null;
      }

      return {
        placeId,
        primaryText: primaryText || fullText,
        secondaryText,
        fullText,
      };
    })
    .filter((suggestion): suggestion is AddressSuggestion =>
      Boolean(suggestion),
    );
};

export const fetchResolvedAddress = async (
  placeId: string,
  apiKey: string,
): Promise<ResolvedAddress> => {
  const response = await fetch(
    `${PLACES_BASE_URL}/places/${encodeURIComponent(placeId)}`,
    {
      method: "GET",
      headers: {
        "X-Goog-Api-Key": apiKey,
        "X-Goog-FieldMask": "addressComponents,formattedAddress",
      },
    },
  );

  if (!response.ok) {
    const details = await extractApiErrorMessage(response);
    throw new Error(`Place details failed: ${details}`);
  }

  const payload = (await response.json()) as GooglePlaceDetailsResponse;
  return parseAddress(payload);
};
